import { HALResponse } from 'ng/src/app/shared/model/app.model';
import { InfiniteScroll, PageOptions, SortFieldArray } from "selectize";

declare let Selectize: any;

Selectize.define('infinite_scroll', function(this: InfiniteScroll, options: PageOptions) {
    this.maxPages = options.maxPages || 1;
    this.page = options.page || 0;

    this.infinitescroll = {
		onScroll: () => {
			let $dropdown = this.$dropdown_content!,
				scrollBottom = $dropdown[0].scrollHeight - ($dropdown.scrollTop()! + $dropdown.height()!);
			
            if (scrollBottom < 300) {
                let query = JSON.stringify({
                    search: this.lastValue,
                    page: this.page
                });

				$dropdown.off('scroll');
				
				this.onSearchChange(query);
            }
        }
    };

    this.onFocus = (() => {
        let original = this.onFocus;

        return () => {
			original!.apply(this);

			let query = JSON.stringify({
                search: this.lastValue,
                page: this.page
            });
			
			this.onSearchChange(query);
        };
    })();

    this.onKeyUp = (e: JQuery.Event) => {
		if (this.isLocked)
			return e && e.preventDefault();
		
        let value = this.$control_input!.val() as string || '';

		if (this.lastValue === value)
			return;

		let query = JSON.stringify({
			search: value,
			page: 0
		});

		this.lastValue = value;
		this.onSearchChange(query);
		this.clearOptions();
		this.refreshOptions(true);
		this.trigger('type', value);
    };

    this.on('load', () => {
        this.page++;
        this.$dropdown_content!.on('scroll', this.infinitescroll!.onScroll);
    });
    
	this.on('blur', () => this.page = 0);
	
	// TODO: import this function via webpack exports-loader to avoid duplication
    /**
     * Highlight text inline that match passed regex pattern. Function copied from Selectize source code.
     * @param $element
     * @param pattern
     * @returns {*}
     */
    this.highlight = ($element: JQuery, pattern: string) => {
        if (typeof pattern === 'string' && !pattern.length) return;
        let regex = (typeof pattern === 'string') ? new RegExp(pattern, 'i') : pattern;

        let highlight = (node: any) => {
            let skip = 0;
            // Wrap matching part of text node with highlighting <span>, e.g.
            // Soccer  ->  <span class="highlight">Soc</span>cer  for regex = /soc/i
            if (node.nodeType === 3) {
                let pos = node.data.search(regex);
                console.log(pos);
                if (pos >= 0 && node.data.length > 0) {
                    let match = node.data.match(regex);
                    let spannode = document.createElement('span');
                    spannode.className = 'highlight';
                    let middlebit = node.splitText(pos);
                    let endbit = middlebit.splitText(match[0].length);
                    let middleclone = middlebit.cloneNode(true);
                    spannode.appendChild(middleclone);
                    middlebit.parentNode.replaceChild(spannode, middlebit);
                    skip = 1;
                }
            }
            // Recurse element node, looking for child text nodes to highlight, unless element
            // is childless, <script>, <style>, or already highlighted: <span class="hightlight">
            else if (node.nodeType === 1 && node.childNodes && !/(script|style)/i.test(node.tagName) && ( node.className !== 'highlight' || node.tagName !== 'SPAN' )) {
                for (let i = 0; i < node.childNodes.length; ++i) {
                    i += highlight(node.childNodes[i]);
                }
            }
            return skip;
        };

        return $element.each(item => {
            highlight(this);
        });
    };

	this.on('dropdown_close', () => {
		if (this.isFocused)
			return;

        // Resets search params to avoid empty searches returning previous results
		this.lastValue = '';
		this.page = 0;
		this.clearOptions();
		this.refreshOptions(false);
    });
});

// TODO: Rename class and all call references to InfiniteScrollOptions
interface SelectizeSettings extends Selectize.IOptions<any, any> { }

/**
 * Options Object for lb_infinite_scroll plugin
 * @param {String} entityType - Entity Type to retrieve from LogBook
 * @param {String} queryField - Field that will be matched against as a user types in Selectize
 * @param {String} valueField - Field to be used as values from the object when queried from a selectize instance
 * @param {String} labelField - Field to be shown as the value in Selectize's dropdown and input selector
 */
class SelectizeSettings {
    private apiUrlChanged = false;
    private apiUrl = 'api/' + this.entityType;
    private hasWildCard = false;
	instances = []; // TODO: Is this actually populated somewhere? 
    private logId: number = 0;
    private onInstanceCallback?: () => void;
	private pageSize = 20;
	private postTransform?: Function;
    private shouldUseLog = false;
	private transformer = (response: HALResponse<{ [entityType: string]: any }>) => {
		return response._embedded[this.entityType];
	};
	private urlParameters: { [key: string]: string | number } = {};
    private wildcard = '';
	private $httpOptions: { [key: string]: any } = {};
	
	[key: string]: any;
	
	constructor(
		private entityType: string,
		private queryField: string,
		valueField: string,
		labelField: string
	) {
		this.hideSelected = true;
		this.labelField = labelField;
		this.loadThrottle = 300;
		this.persist = false;
		this.sortField = [];
		this.valueField = valueField;
	}
	
	/**
	 * Sets the field that selectize will query from API
	 * @param {String} newQueryField - case sensitive
	 * @return {SelectizeSettings}
	 */
	setQueryField(newQueryField: string) {
		this.queryField = newQueryField;
		
        return this;
    }
	
	/**
	 * The field to be used as values from the object when queried from a selectize instance
	 * @param {String} newValueField - case sensitive
	 * @return {SelectizeSettings}
	 */
    setValueField(newValueField: string) {
		this.valueField = newValueField;
		
        return this;
    };
	
	/**
	* The field to be shown as the value in Selectize's dropdown and input selector
	* @param newLabelField - case sensitive
	* @return {SelectizeSettings}
	*/
    setLabelField(newLabelField: string) {
		this.labelField = newLabelField;
		
        return this;
    };
	
	/**
	 * Fields on object to be used for searching on cached objects
	 * @param {String[]} newSearchField - case sensitive
	 * @return {SelectizeSettings}
	 */
    setSearchField(newSearchField: string) {
		this.searchField = newSearchField;
		
        return this;
    };
	
	/**
	 * Adds a field for selectize to sort on. An optional direction can be supplied.
	 * @param {String} sortField
	 * @param {String=} direction
	 * @return {SelectizeSettings}
	 */
    addSortField(sortField: string, direction: 'asc' | 'desc') {
        (this.sortField as SortFieldArray).push({
			field: sortField,
			direction: direction
		});
        
        return this;
    };
	
	/**
	 * Sets the amount of time in-between keystrokes or after element focus before a new page of items is fetched
	 * @param {Number} newLoadThrottle - Number of milliseconds
	 * @return {SelectizeSettings}
	 */
    setLoadThrottle(newLoadThrottle: number) {
		this.loadThrottle = newLoadThrottle;
        
        return this;
    };
	
	/**
	 * Sets callback function for when the value of the component is changed
	 * @param {Function} callBack - new onChange callback function
	 * @return {SelectizeSettings}
	 */
    setOnChange(callBack: (value: string) => void) {
		this.onChange = callBack;
        
        return this;
	};
	
	/**
	 * Sets a results transformer from $http requests unwrapped from data
	 * @param {Function} newTransformer
	 * @return {SelectizeSettings}
	 */
    setTransformer(newTransformer: (response: HALResponse<{ [entityType: string]: any }>) => any) {
		this.transformer = newTransformer;
		
        return this;
    };
	
	/**
	 * Sets a function to be called after the transformer has completed;
	 * @param {Function} newPostTransformer
	 * @return {SelectizeSettings}
	 */
    setPostTransformer(newPostTransformer: Function) {
		this.postTransform = newPostTransformer;
		
        return this;
    };
    
    /**
     * Sets the entity type to be used when fetching from default API configuration. No effect when custom API Url is set.
     * @param {String} newEntityType - case sensitive
     * @return {SelectizeSettings}
     */
    setEntityType(newEntityType: string) {
		this.entityType = newEntityType;
		
        if (!this.apiUrlChanged) {
            this.setApiUrl('api/' + this.entityType);
            this.apiUrlChanged = false;
		}
		
        return this;
    };
	
	/**
	 * Overrides the API Url constructor and set custom API Url. To set parameters
	 * @param {String} newApiUrl
	 * @return {SelectizeSettings}
	 */
    setApiUrl(newApiUrl: string) {
        this.apiUrl = newApiUrl;
		this.apiUrlChanged = true;
		
        return this;
    };
    
    /**
     * Overrides the Log ID from being obtained through RootScope on initialization
     * @param {Number} newLogId
     * @return {SelectizeSettings}
     */
    setLogId(newLogId: number) {
		this.logId = newLogId;
        
        return this;
    };
    
    /**
     * What to wrap search queries with
     * @param {String} newWildCard
     * @return {SelectizeSettings}
     */
    setWildCard(newWildCard: string) {
		this.wildcard = newWildCard;
		this.hasWildCard = true;
        
        return this;
    };
    
    /**
     * Sets page size to retrieve per query
     * @param {number} newPageSize
     * @return {SelectizeSettings}
     */
    setPageSize(newPageSize: number) {
		this.pageSize = newPageSize;
		
        return this;
    };
    
    /**
     * Sets the $Http Option to invoke with requests. The Params option is overridden with values from UrlParameters
     * @param {Object} new$httpOptions
     * @return {SelectizeSettings}
     */
	set$httpOptions(new$httpOptions: { [key: string]: any }) {
		this.$httpOptions = new$httpOptions;
		
        return this;
    };
    
    /**
     * Adds a URL Parameter to URL Parameter object
     * @param {String} key
     * @param value
     * @return {SelectizeSettings}
     */
    addUrlParameter(key: string, value: string | number) {
		this.urlParameters[key] = value;
		
        return this;
    };
    
    /**
     * Adds a key and value to the $http Options object used when sending requests
     * @param {String} key
     * @param value
     * @return {SelectizeSettings}
     */
    add$httpOption(key: string, value: any) {
		this.$httpOptions[key] = value;
		
        return this;
    };
    
    /**
     * Sets a callback function to be executed with the context of the instance on one being created
     * @param {function} callback - Function with context being that of the instance
     * @return {SelectizeSettings}
     */
    setOnInstanceCreated(callback: () => void) {
		this.onInstanceCallback = callback;
        
        return this;
    };
    
    /**
     * Adds a custom option to be used on Selectize initialization that is not provided here. Check Selectize Usage documentation.
     * @param {String} key
     * @param value
     * @return {SelectizeSettings}
     */
    addCustom(key: string, value: any) {
		this[key] = value;
		
        return this;
    };
    
    /**
     * Getter/Setter for whether to send log ID with requests
     * @param {Boolean=} shouldUseLog
     * @return {boolean|SelectizeSettings}
     */
    useLog(shouldUseLog?: boolean) {
        if (typeof shouldUseLog == 'boolean') {
			this.shouldUseLog = shouldUseLog;
			
            return this;
		}
		
        return this.shouldUseLog;
    };
    
    getPageSize() {
        return this.pageSize;
    };
    
    getTransform() {
        return this.transformer;
    };
    
    getApiUrl() {
        return this.apiUrl;
    };
    
    getEntityType() {
        return this.entityType;
    };
    
    /**
     * Builds current $http Options with params set to current URL Parameters
     */
    get$httpOptions() {
		let options = this.$httpOptions;
		
        options.params = this.urlParameters;
		options.params.size = this.pageSize;
        
        return options;
    };
    
    getQueryField() {
        return this.queryField;
    };
    
    getSearchField() {
        return this.searchField || [this.labelField];
    };
    
    getValueField() {
        return this.valueField;
    };
    
    getLabelField() {
        return this.labelField;
    };
    
    getLogId() {
        return this.logId;
    };
    
    /**
     * Wraps a provided query with a wild card is set
     * @param {String} query
     * @return {string}
     */
    wrapWithWildCard(query: string) {
        if (this.hasWildCard)
			query = this.wildcard + query + this.wildcard;
		
        return query;
    };
    
    /**
     * Executes the instance creation callback with context being the instance
     * @param instance
     */
	executeInstanceCallback(instance: Object) {
		if (this.onInstanceCallback)
			this.onInstanceCallback.call(instance);
	};
    
    /**
     * Calls the transformer function with the context set to this object.
     * @param {[]} response
     */
    executeTransformer(response: HALResponse<{ [entityType: string]: any }>) {
        return this.transformer.call(this, response);
    };
    
    /**
     * Calls the post transformer function the context set to this object.
     */
    executePostTransformer() {
        if (this.postTransform)
            this.postTransform.call(this);
    };
};

/**
 * Loads tags that belong to report that were not in the initial load of the tag selector
 * @param instance - Selectize instance to load options into
 * @param {number} reportId - Id of the report
 * @param {number} reportFieldId - Id of the field
 * @param {'tagValues' | 'choiceListItems' } reportFieldName indicates which reportFields to grab
 */
let loadReportFields = (instance: InfiniteScroll, reportId: number, reportFieldId: number, reportFieldName: string) => {
    $.ajax({
		url: 'api/reportFields?' + $.param({
			report: reportId,
			id: reportFieldId
		}),
		dataType: 'json',
		
		// TODO: Model report fields, add types below

        success: data => {
			let items = (data._embedded.reportFields.length) ? data._embedded.reportFields[0] : [],
				isTagValues = reportFieldName == 'tagValues';

			let option = isTagValues ?
				items.tagValues :
				items.contains.map((item: any) => ({ value: item }));
			
			let itemsToAdd = isTagValues ?
				items.tagValues.map((tag: any) => tag.id) :
				items.contains;

            instance.addOption(option);
			instance.addItems(itemsToAdd);
        }
    });
};

export { SelectizeSettings, loadReportFields };
